import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, HelpCircle, Clock, MapPin, ArrowRight } from 'lucide-react';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { ContactForm } from '../components/static/ContactForm';
import { FAQAccordion } from '../components/static/FAQAccordion';
import { Button } from '../components/shared/Button';
import { SectionHeading } from '../components/shared/SectionHeading';

export const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleScrollToFAQ = () => {
    const faqSection = document.getElementById('faq');
    if (faqSection) {
      faqSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="page-shell">
      <Header />

      <section className="section-shell">
        <div className="section-container">
          <div className="surface-card-soft p-8 md:p-12">
            <SectionHeading
              align="center"
              eyebrow={t('contact.eyebrow')}
              title={t('contact.heroTitle')}
              subtitle={t('contact.heroSubtitle')}
            />
            <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-2xl border border-primary/10 bg-white px-4 py-3 text-sm text-slate-600">
                <span className="inline-flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  {t('contact.responseTime')}
                </span>
              </div>
              <div className="rounded-2xl border border-primary/10 bg-white px-4 py-3 text-sm text-slate-600">
                <span className="inline-flex items-center gap-2">
                  <Mail className="h-4 w-4 text-primary" />
                  support@inburgertest.nl
                </span>
              </div>
              <div className="rounded-2xl border border-primary/10 bg-white px-4 py-3 text-sm text-slate-600">
                <span className="inline-flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  {t('contact.location')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell">
        <div className="section-container">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-12">
            <div>
              <h2 className="text-3xl font-bold text-ink">{t('contact.sendMessage')}</h2>
              <p className="mb-6 mt-2 text-slate-600">
                {t('contact.sendMessageSubtitle')}
              </p>
              <ContactForm />
            </div>

            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-ink">{t('contact.otherWays')}</h2>
              <p className="mt-2 text-slate-600">
                {t('contact.otherWaysSubtitle')}
              </p>

              <div className="surface-card p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-2 text-xl font-bold text-ink">{t('contact.emailUs')}</h3>
                    <p className="mb-3 text-slate-600">
                      {t('contact.emailDescription')}
                    </p>
                    <a
                      href="mailto:support@inburgertest.nl"
                      className="text-lg font-medium text-primary hover:underline"
                    >
                      support@inburgertest.nl
                    </a>
                    <p className="mt-2 flex items-center gap-1 text-sm text-slate-500">
                      <Clock className="h-4 w-4" />
                      {t('contact.weReply')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="surface-card p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <HelpCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-2 text-xl font-bold text-ink">{t('contact.faqCard')}</h3>
                    <p className="mb-4 text-slate-600">
                      {t('contact.faqDescription')}
                    </p>
                    <button
                      onClick={handleScrollToFAQ}
                      className="flex items-center gap-1 font-medium text-primary hover:underline"
                    >
                      {t('contact.viewFaq')}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="section-shell scroll-mt-16">
        <div className="section-container">
          <SectionHeading
            align="center"
            eyebrow={t('contact.faqEyebrow')}
            title={t('contact.faqTitle')}
            subtitle={t('contact.faqSubtitle')}
          />
          <div className="surface-card mt-10 p-6 md:p-8">
            <FAQAccordion />
          </div>
          
          <div className="mt-6 rounded-2xl border border-primary/10 bg-white p-6 text-center">
            <p className="mb-4 text-slate-700">
              {t('contact.questionNotFound')}
            </p>
            <Button 
              variant="primary"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              {t('contact.sendAMessage')}
            </Button>
          </div>
        </div>
      </section>

      <section className="section-shell">
        <div className="section-container">
          <div className="surface-card-soft px-6 py-12 text-center md:px-12">
          <h2 className="text-3xl font-bold text-ink md:text-4xl">
            {t('contact.ctaTitle')}
          </h2>
          <p className="mb-8 mt-4 text-lg text-slate-600">
            {t('contact.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="primary"
              size="large"
              onClick={() => navigate('/try-free')}
            >
              {t('contact.startFreeExam')}
            </Button>
            <Button 
              variant="secondary"
              size="large"
              onClick={() => navigate('/about')}
            >
              {t('contact.moreAboutUs')}
            </Button>
          </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
